import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { SURVEY_LIST_PAGE_SIZE } from '../constants/survey.constants';
import type { SurveyListParams } from '../types/survey.types';
import { toSurveyFilterSearch, toSurveyFilterValues } from '../utils/surveyFilters';

const route = getRouteApi('/_auth/feedback/surveys/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toSurveyFilterValues(toSurveyFilterSearch(values));

export function useSurveyFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toSurveyFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toSurveyFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const titleSearch = useUrlSyncedSearch({
    appliedText: search.q,
    onApply: (text) => {
      void navigate({ search: (previous) => ({ ...previous, q: text }), replace: true });
    },
  });

  const hasActiveFilters = [search.q, search.state, search.from, search.to].some(
    (value) => value !== undefined,
  );

  const listQueryParams = useMemo<Omit<SurveyListParams, 'page'>>(
    () => ({
      search: search.q,
      status: search.state,
      startDate: search.from,
      endDate: search.to,
      limit: SURVEY_LIST_PAGE_SIZE,
    }),
    [search],
  );

  return { ...filters, ...titleSearch, search, hasActiveFilters, listQueryParams };
}
